import React, { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Upload, Loader2 } from "lucide-react";
import { ZohoCallTokenCard } from "@/components/helm/ZohoCallTokenCard";
import { useAuth } from "@/context/AuthContext";

async function blobUrl(path) {
  try {
    const res = await api.get(path, { responseType: "blob" });
    return URL.createObjectURL(res.data);
  } catch { return null; }
}

export default function HelmSettings() {
  const [id, setId] = useState(null);
  const [phone, setPhone] = useState("");
  const [agencyName, setAgencyName] = useState("");
  const [photo, setPhoto] = useState(null);
  const [logo, setLogo] = useState(null);
  const [me, setMe] = useState(null);
  const [busy, setBusy] = useState("");
  const photoRef = useRef(); const logoRef = useRef();
  const { user } = useAuth();

  const load = async () => {
    const { data } = await api.get("/agent/identity");
    setId(data); setPhone(data.agent.phone || ""); setAgencyName(data.agency.name || "");
    const meRes = await api.get("/auth/me").catch(() => null);
    const uid = meRes?.data?.id || meRes?.data?.user?.id;
    setMe(uid);
    if (data.agent.has_photo && uid) setPhoto(await blobUrl(`/agent/identity/${uid}/photo`));
    if (data.agency.has_logo) setLogo(await blobUrl(`/agent/agency/logo`));
  };
  useEffect(() => { load(); }, []);

  const saveContact = async () => { setBusy("contact"); try { await api.patch("/agent/identity", { phone }); } finally { setBusy(""); } };
  const saveAgency = async () => { setBusy("agency"); try { await api.patch("/agent/agency", { name: agencyName }); } finally { setBusy(""); } };
  const uploadPhoto = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; setBusy("photo");
    try { const fd = new FormData(); fd.append("file", f); await api.post("/agent/identity/photo", fd, { headers: { "Content-Type": "multipart/form-data" } }); await load(); }
    finally { setBusy(""); if (photoRef.current) photoRef.current.value = ""; }
  };
  const uploadLogo = async (e) => {
    const f = e.target.files?.[0]; if (!f) return; setBusy("logo");
    try { const fd = new FormData(); fd.append("file", f); await api.post("/agent/agency/logo", fd, { headers: { "Content-Type": "multipart/form-data" } }); await load(); }
    finally { setBusy(""); if (logoRef.current) logoRef.current.value = ""; }
  };

  if (!id) return <div className="p-6 sm:p-10 max-w-3xl mx-auto"><Skeleton className="h-96 w-full rounded-xl" /></div>;

  return (
    <div className="p-6 sm:p-10 max-w-3xl mx-auto space-y-6">
      <div>
        <h1 className="font-display font-bold text-3xl lg:text-4xl text-[#0B192C] tracking-tight" data-testid="settings-title">Identity & Settings</h1>
        <p className="text-muted-foreground mt-1.5">Your profile appears on the workspace and in client hand-offs.</p>
      </div>

      {user?.role === "platform_admin" && <ZohoCallTokenCard />}

      <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-5" data-testid="settings-agent">
        <h2 className="text-lg font-bold text-[#0B192C] mb-4">Agent profile</h2>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-full bg-[#0B192C] text-[#D4AF37] flex items-center justify-center overflow-hidden shrink-0">
            {photo ? <img src={photo} alt="" className="w-full h-full object-cover" data-testid="settings-photo-preview" /> : <span className="font-bold text-xl">{(id.agent.name || "A")[0]}</span>}
          </div>
          <input ref={photoRef} type="file" accept="image/*" className="hidden" onChange={uploadPhoto} data-testid="settings-photo-input" />
          <Button variant="outline" onClick={() => photoRef.current?.click()} disabled={busy === "photo"} data-testid="settings-photo-btn">
            {busy === "photo" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload photo
          </Button>
        </div>
        <div className="grid sm:grid-cols-2 gap-3">
          <div><Label className="text-xs">Name</Label><Input value={id.agent.name || ""} disabled className="h-10 mt-1 bg-[#F8F8F5]" data-testid="settings-name" /></div>
          <div><Label className="text-xs">Email</Label><Input value={id.agent.email || ""} disabled className="h-10 mt-1 bg-[#F8F8F5]" data-testid="settings-email" /></div>
          <div><Label className="text-xs">NPN</Label><Input value={id.agent.npn || "—"} disabled className="h-10 mt-1 bg-[#F8F8F5]" data-testid="settings-npn" /></div>
          <div><Label className="text-xs">Direct line</Label><Input value={phone} onChange={(e) => setPhone(e.target.value)} className="h-10 mt-1 bg-white" data-testid="settings-phone" /></div>
        </div>
        <Button onClick={saveContact} disabled={busy === "contact"} className="mt-4 h-10 bg-[#0B192C] text-white" data-testid="settings-save-contact">Save contact details</Button>
      </div>

      <div className="rounded-xl border-2 border-[#E5E5E0] bg-white p-5" data-testid="settings-agency">
        <h2 className="text-lg font-bold text-[#0B192C] mb-1">Agency</h2>
        <p className="text-xs text-[#9CA3AF] mb-4">{id.is_agency_owner ? "You control the agency name and logo for the whole workspace." : "Agency branding is controlled by the agency owner."}</p>
        <div className="flex items-center gap-4 mb-4">
          <div className="w-16 h-16 rounded-xl bg-[#F8F8F5] border flex items-center justify-center overflow-hidden shrink-0">
            {logo ? <img src={logo} alt="" className="w-full h-full object-contain" data-testid="settings-logo-preview" /> : <span className="text-xs text-[#9CA3AF]">No logo</span>}
          </div>
          {id.is_agency_owner && (
            <>
              <input ref={logoRef} type="file" accept="image/*" className="hidden" onChange={uploadLogo} data-testid="settings-logo-input" />
              <Button variant="outline" onClick={() => logoRef.current?.click()} disabled={busy === "logo"} data-testid="settings-logo-btn">
                {busy === "logo" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Upload className="w-4 h-4 mr-1" />} Upload logo
              </Button>
            </>
          )}
        </div>
        <div>
          <Label className="text-xs">Agency name</Label>
          <Input value={agencyName} onChange={(e) => setAgencyName(e.target.value)} disabled={!id.is_agency_owner} className="h-10 mt-1 bg-white max-w-md" data-testid="settings-agency-name" />
        </div>
        {id.is_agency_owner && <Button onClick={saveAgency} disabled={busy === "agency"} className="mt-4 h-10 bg-[#0B192C] text-white" data-testid="settings-save-agency">Save agency</Button>}
      </div>
    </div>
  );
}
